import { useNotify, useDataProvider } from 'react-admin';
import { useEffect, useState } from "react";
import InputFieldCustom from '../Commons/InputFieldCustom'
import { useMediaQuery, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Validate from '../Commons/Validate';
import CircularProgress from '@mui/material/CircularProgress';
import BreadCrumbCustom from '../Commons/BreadCrum';
import historyTracker from '../Commons/historyTracker';
import { useNavigate, useLocation } from 'react-router-dom';
import { Style } from '../Commons/Styles.jsx';
import BottomToolBar from '../Commons/BottomToolBar';
import AuthNotification from '../Commons/AuthNotification';
import InternalMenu from '../Commons/InternalMenu';
// TODO: reemplazar por el ícono definitivo de Proveedores cuando esté disponible
import providers from '../Commons/Icons/apps.svg';
import providerList from '../Commons/Icons/book.svg';

import { hasPermissions } from '../Commons/CompanyAuth';

function Alert(props) {
    return <MuiAlert elevation={6} variant="filled" {...props} />;
}

// asistelibros (char(2)) aca NO es el mismo dominio de 3 valores usado en Gastos Contables
// (BA/SA/IP, ver comentario en AccountingExpenseCreate.jsx): sp_Alta_cbfacturainteginv
// documenta explicitamente en un comentario junto al parametro que para integracion de
// inventario son 4 valores fijos -- BA (Bienes Afectos), BE (Bienes Exentos), SA (Servicios
// Afectos), SE (Servicios Exentos) -- para el mismo Libro de Compras (SAT). Tampoco tiene
// catalogo propio en BD, es obligatorio igual que en Gastos Contables. Solo se ofrecen BA/BE
// aca: una Integracion de Inventario es siempre un Bien (mercaderia que entra a existencias),
// nunca un Servicio, asi que SA/SE quedan fuera de esta pantalla a pedido del usuario.
const BooksFlagOptions = [
    { key: 'BA', value: 'Bienes Afectos' },
    { key: 'BE', value: 'Bienes Exentos' },
];

// La factura (corre_cbfactura) llega desde InventoryIntegrationsList por query string
// (?invoice=...&invoiceNumber=...), no por location.state: el correlativo nunca se
// muestra en este formulario, solo se manda al crear.
const getInvoice = (location) => {
    try {
        const params = new URLSearchParams(location?.search || '');
        const fromQuery = params.get('invoice');
        if (fromQuery) return fromQuery;
    } catch {
        // sigue al respaldo de abajo
    }
    return location?.state?.invoiceId || null;
};

const getInvoiceNumber = (location) => {
    try {
        const params = new URLSearchParams(location?.search || '');
        const fromQuery = params.get('invoiceNumber');
        if (fromQuery) return fromQuery;
    } catch {
        // sigue al respaldo de abajo
    }
    return location?.state?.invoiceNumber || null;
};

const emptySerialRow = () => ({ quantity: '', serialNumber: '', manufactureDate: '', expirationDate: '' });

const InventoryIntegrationCreate = (props) => {
    const isSmall = useMediaQuery(theme => theme.breakpoints.down('sm'));
    const navigate = useNavigate();
    const location = useLocation();
    const invoiceId = getInvoice(location);
    const invoiceNumber = getInvoiceNumber(location);

    // Arbol de catalogos: TipoInventario -> ClasificacionArticulos (recursivo, profundidad
    // variable) -> Categoria -> Articulo. classificationLevels es un arreglo dinamico: cada
    // vez que se elige un nodo con hijos (childrenCount > 0) se agrega un nivel nuevo con las
    // opciones de sus hijos; al elegir un nodo hoja (childrenCount === 0) se cargan las
    // categorias de esa clasificacion en vez de agregar otro nivel.
    const [inventoryTypes, setInventoryTypes] = useState([]);
    // Catalogo crudo de tipos de inventario (id + code/description/type), guardado aparte de
    // "inventoryTypes" (que solo tiene key/value para el <select>) para poder leer el campo
    // "type" (MP/PT/PP/ME/IP/AF/SE/ST) del tipo elegido: ese valor decide si el articulo
    // puede tener atributos/numeros de serie (isTrackedType, ver mas abajo).
    const [inventoryTypesRaw, setInventoryTypesRaw] = useState([]);
    const [inventoryType, setInventoryType] = useState('');
    const [classificationLevels, setClassificationLevels] = useState([]);
    const [categories, setCategories] = useState([]);
    const [category, setCategory] = useState('');
    const [articles, setArticles] = useState([]);
    const [article, setArticle] = useState('');

    const [warehouses, setWarehouses] = useState([]);
    const [warehouse, setWarehouse] = useState('');
    const [reasons, setReasons] = useState([]);
    const [reason, setReason] = useState('');

    const [quantity, setQuantity] = useState('');
    const [amount, setAmount] = useState('0.00');
    const [vatExemptAmount, setVatExemptAmount] = useState('0.00');
    const [isrExemptAmount, setIsrExemptAmount] = useState('0.00');
    const [isrCode, setIsrCode] = useState('');
    const [isrConcepts, setIsrConcepts] = useState([]);
    // Requerido (ver nota en BooksFlagOptions): arranca en la primera opcion para que el
    // <select> nativo y el estado queden sincronizados desde el primer render.
    const [booksFlag, setBooksFlag] = useState(BooksFlagOptions[0].key);

    // Monto total de la factura y lo que ya se registro en ella, para validar que esta
    // integracion (pueden ser varias parciales) nunca haga que la suma supere el monto de la
    // factura -- mismo patron que AccountingExpenseCreate.jsx. cbfactura.montointegraciongasto
    // (Gastos Contables) y montointegracioninventarios (esta pantalla) son columnas separadas
    // en BD, pero ambas restan del MISMO monto de la factura: si ya hay Gastos Contables
    // registrados, el saldo pendiente para crear una integracion de inventario debe reflejarlo
    // (y viceversa), asi que registeredAmount suma ambas fuentes (ver donde se calcula).
    const [invoiceAmount, setInvoiceAmount] = useState(null);
    const [registeredAmount, setRegisteredAmount] = useState(0);

    // Atributos dinamicos del articulo elegido (solo aplica a tipo de inventario "ME", ver
    // isTrackedType mas abajo). attributeValues esta indexado por el id del "slot" de
    // atributo (corre_ciarticulosatributos) y guarda {domainId, value} listo para mandar.
    const [attributeSlots, setAttributeSlots] = useState([]);
    const [attributeDomains, setAttributeDomains] = useState([]);
    const [attributeValues, setAttributeValues] = useState({});

    // Numeros de serie / fechas por unidad, cuando la categoria del articulo lo exige
    // (registroindividual/fechafabricacion/fechavencimiento, solo relevantes si el tipo de
    // inventario es "ME"). Se valida que la suma de "cantidad" de las filas coincida con la
    // Cantidad total capturada arriba, igual que exige sp_Alta_cbfacturainteginv.
    const [serialRows, setSerialRows] = useState([emptySerialRow()]);

    const [openerror, setOpenerror] = useState(false);
    const [message, setMessage] = useState('');
    const [opacity, setOpacity] = useState(1);
    const [displayLoading, setDisplayLoading] = useState('none');
    const [disable, setDisable] = useState(false);
    const [openAuth, setOpenAuth] = useState(false);
    const [auth, setAuth] = useState(false);
    // Mientras esto es true se muestra el spinner de carga inicial (ver el bloque
    // "initialLoading" en el return): esta pantalla necesita traer permisos y todos los
    // catalogos (tipos de inventario, almacenes, razones, conceptos ISR, factura) antes de
    // poder mostrar el formulario, y sin este estado la ventana se veia en blanco (solo el
    // breadcrumb) durante esa espera.
    const [initialLoading, setInitialLoading] = useState(true);
    const dataProvider = useDataProvider();

    const notify = useNotify();

    const handleClickError = () => {
        setOpenerror(true);
    };

    const handleCloseError = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }

        setOpenerror(false);
    };

    useEffect(() => {
        (async () => {
            try {
                const currentPath = location.pathname;
                historyTracker.addPath(currentPath, invoiceNumber ? `Crear Integración de Inventario - Factura ${invoiceNumber}` : 'Crear Integración de Inventario', 7);

                const authdata = { data: hasPermissions('InventoryIntegration', 'create') };
                if (authdata.data.length > 0) {
                    setAuth(authdata.data[0]);
                    setOpenAuth(!authdata.data[0]);

                    if (authdata.data[0]) {
                        // Promise.allSettled en vez de Promise.all: si UN catalogo falla (por
                        // ejemplo un error de SQL en uno solo de ellos), antes se perdian los otros
                        // cuatro tambien porque Promise.all rechaza todo el bloque apenas una
                        // promesa falla, y el catch de arriba lo tragaba en silencio sin dejar
                        // rastro. Cada catalogo ahora se resuelve de forma independiente y el que
                        // falle queda registrado en consola con su propio error, en vez de dejar
                        // los selects vacios sin ninguna pista de cual fue el que realmente fallo.
                        // Se incluyen tambien la factura y lo que ya se registro en ella, para
                        // calcular el saldo pendiente (ver comentario en invoiceAmount/
                        // registeredAmount mas arriba). Ese "ya registrado" son DOS sumas
                        // independientes que comparten el mismo saldo de la factura: las
                        // integraciones de inventario propias (montointegracioninventarios) Y los
                        // gastos contables (montointegraciongasto) -- son columnas separadas en
                        // cbfactura, pero ambas restan del mismo monto total de la factura, asi que
                        // el saldo pendiente para crear una integracion nueva debe descontar las dos,
                        // no solo las integraciones de inventario ya creadas.
                        const [typesResult, warehousesResult, reasonsResult, isrConceptsResult, invoiceResult, existingIntegrationsResult, existingExpensesResult] = await Promise.allSettled([
                            dataProvider.getList('provider/inventorytypes', { filter: {}, pagination: {}, sort: {} }),
                            dataProvider.getList('provider/warehouses', { filter: {}, pagination: {}, sort: {} }),
                            dataProvider.getList('provider/inventoryreasons', { filter: {}, pagination: {}, sort: {} }),
                            dataProvider.getList('provider/isrconcepts', { filter: {}, pagination: {}, sort: {} }),
                            dataProvider.getOne('provider/invoice', { id: invoiceId }),
                            dataProvider.getList('provider/inventoryintegration', { filter: { invoice: invoiceId }, pagination: {}, sort: {} }),
                            dataProvider.getList('provider/accountingexpense', { filter: { invoice: invoiceId }, pagination: {}, sort: {} }),
                        ]);

                        if (typesResult.status === 'fulfilled') {
                            // Se guarda tanto el catalogo crudo (para poder leer "type" -- MP/PT/PP/
                            // ME/IP/AF/SE/ST -- del tipo elegido, que decide si aplican los
                            // subformularios de atributos/numeros de serie) como la version
                            // key/value para el <select>.
                            setInventoryTypesRaw(typesResult.value.data);
                            var typesList = typesResult.value.data.map(item => ({ key: item.id, value: `${item.code} - ${item.description}` }));
                            setInventoryTypes([{ key: '', value: 'Seleccione' }, ...typesList]);
                        } else {
                            console.error('No se pudo cargar el catálogo de Tipos de Inventario:', typesResult.reason);
                            notify('No se pudo cargar el catálogo de Tipos de Inventario.', { type: 'warning' });
                        }

                        if (warehousesResult.status === 'fulfilled') {
                            var warehousesList = warehousesResult.value.data.map(item => ({ key: item.id, value: item.name }));
                            setWarehouses([{ key: '', value: 'Seleccione' }, ...warehousesList]);
                        } else {
                            console.error('No se pudo cargar el catálogo de Almacenes:', warehousesResult.reason);
                            notify('No se pudo cargar el catálogo de Almacenes.', { type: 'warning' });
                        }

                        if (reasonsResult.status === 'fulfilled') {
                            // Solo razones marcadas para "Compras" aplican a una integracion de
                            // inventario originada en una factura de proveedor (mismo patron "traer
                            // todo y filtrar en cliente" que Centro/Cuenta en AccountingExpenseCreate).
                            var reasonsList = reasonsResult.value.data.filter(item => item.compras).map(item => ({ key: item.id, value: item.description }));
                            setReasons([{ key: '', value: 'Seleccione' }, ...reasonsList]);
                        } else {
                            console.error('No se pudo cargar el catálogo de Razones:', reasonsResult.reason);
                            notify('No se pudo cargar el catálogo de Razones.', { type: 'warning' });
                        }

                        if (isrConceptsResult.status === 'fulfilled') {
                            // Concepto ISR: si hay uno marcado "default", se preselecciona para que
                            // coincida con lo que haria la SP si se dejara vacio (aunque aca el
                            // campo es obligatorio, ver validacion mas abajo).
                            var isrConceptsList = isrConceptsResult.value.data.map(item => ({ key: item.id, value: item.name }));
                            setIsrConcepts([{ key: '', value: 'Seleccione' }, ...isrConceptsList]);
                            var defaultIsrConcept = isrConceptsResult.value.data.find(item => item.default);
                            if (defaultIsrConcept)
                                setIsrCode(defaultIsrConcept.id);
                        } else {
                            console.error('No se pudo cargar el catálogo de Conceptos de Retención ISR:', isrConceptsResult.reason);
                            notify('No se pudo cargar el catálogo de Conceptos de Retención ISR.', { type: 'warning' });
                        }

                        if (invoiceResult.status === 'fulfilled') {
                            setInvoiceAmount(invoiceResult.value.data.invoiceAmount);
                        } else {
                            console.error('No se pudo cargar el monto de la factura:', invoiceResult.reason);
                            setInvoiceAmount(null);
                        }

                        // Las dos sumas (integraciones de inventario + gastos contables) se
                        // combinan en un solo registeredAmount: ver comentario arriba sobre por
                        // que ambas restan del mismo saldo de la factura.
                        var alreadyRegisteredIntegrations = 0;
                        var alreadyRegisteredExpenses = 0;

                        if (existingIntegrationsResult.status === 'fulfilled') {
                            alreadyRegisteredIntegrations = existingIntegrationsResult.value.data.reduce((total, item) => total + parseFloat(item.amount || 0), 0);
                        } else {
                            console.error('No se pudo cargar las integraciones de inventario ya registradas de la factura:', existingIntegrationsResult.reason);
                        }

                        if (existingExpensesResult.status === 'fulfilled') {
                            alreadyRegisteredExpenses = existingExpensesResult.value.data.reduce((total, item) => total + parseFloat(item.amount || 0), 0);
                        } else {
                            console.error('No se pudo cargar los gastos contables ya registrados de la factura:', existingExpensesResult.reason);
                        }

                        if (existingIntegrationsResult.status === 'fulfilled' || existingExpensesResult.status === 'fulfilled') {
                            setRegisteredAmount(alreadyRegisteredIntegrations + alreadyRegisteredExpenses);
                        } else {
                            setRegisteredAmount(0);
                        }
                    }
                } else {
                    setAuth(false);
                    setOpenAuth(true)
                }
            }
            catch {
            }
            finally {
                setInitialLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataProvider, location.pathname]);

    // Al cambiar el Tipo de Inventario se reinicia todo el arbol debajo de el (clasificacion,
    // categoria, articulo, atributos, numeros de serie) y se cargan las clasificaciones raiz
    // (sin "parent") de ese tipo.
    useEffect(() => {
        (async () => {
            setCategories([]);
            setCategory('');
            setArticles([]);
            setArticle('');
            setAttributeSlots([]);
            setAttributeDomains([]);
            setAttributeValues({});
            setSerialRows([emptySerialRow()]);

            if (!inventoryType) {
                setClassificationLevels([]);
                return;
            }

            try {
                const { data } = await dataProvider.getList('provider/articleclassifications', { filter: { type: inventoryType }, pagination: {}, sort: {} });
                setClassificationLevels([{ options: data, selectedId: '' }]);
            } catch {
                setClassificationLevels([]);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inventoryType, dataProvider]);

    // Al elegir un articulo se consultan sus atributos (sp_lista_ciarticulosatributos): si no
    // tiene ninguno, el subformulario de atributos simplemente no se muestra.
    useEffect(() => {
        (async () => {
            setAttributeValues({});
            setSerialRows([emptySerialRow()]);

            if (!article) {
                setAttributeSlots([]);
                setAttributeDomains([]);
                return;
            }

            try {
                const { data } = await dataProvider.getOne('provider/articleattributes', { id: article });
                setAttributeSlots(data.slots || []);
                setAttributeDomains(data.domains || []);
            } catch {
                setAttributeSlots([]);
                setAttributeDomains([]);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [article, dataProvider]);

    // Un nodo de clasificacion con hijos (childrenCount > 0) agrega un nivel nuevo debajo;
    // un nodo hoja (childrenCount === 0) carga las categorias de esa clasificacion. Cambiar
    // la seleccion de un nivel intermedio descarta todos los niveles/seleccion debajo de el.
    const handleSelectClassification = async (levelIndex, selectedId) => {
        const level = classificationLevels[levelIndex];
        const truncated = classificationLevels.slice(0, levelIndex + 1);
        truncated[levelIndex] = { ...truncated[levelIndex], selectedId };
        setClassificationLevels(truncated);
        setCategories([]);
        setCategory('');
        setArticles([]);
        setArticle('');

        if (!selectedId || !level) return;

        const node = level.options.find(item => `${item.id}` === `${selectedId}`);
        if (!node) return;

        if (node.childrenCount > 0) {
            try {
                const { data } = await dataProvider.getList('provider/articleclassifications', { filter: { type: inventoryType, parent: selectedId }, pagination: {}, sort: {} });
                setClassificationLevels([...truncated, { options: data, selectedId: '' }]);
            } catch {
                // no se pudo cargar el siguiente nivel, se deja como esta
            }
        } else {
            try {
                const { data } = await dataProvider.getList('provider/articlecategories', { filter: { classification: selectedId }, pagination: {}, sort: {} });
                setCategories(data);
            } catch {
                setCategories([]);
            }
        }
    };

    const handleSelectCategory = async (selectedId) => {
        setCategory(selectedId);
        setArticles([]);
        setArticle('');

        if (!selectedId) return;

        try {
            const { data } = await dataProvider.getList('provider/articles', { filter: { category: selectedId }, pagination: {}, sort: {} });
            setArticles(data);
        } catch {
            setArticles([]);
        }
    };

    const updateSerialRow = (index, field, value) => {
        setSerialRows(prev => prev.map((row, i) => i === index ? { ...row, [field]: value } : row));
    };

    const addSerialRow = () => setSerialRows(prev => [...prev, emptySerialRow()]);

    const removeSerialRow = (index) => setSerialRows(prev => prev.length > 1 ? prev.filter((_, i) => i !== index) : prev);

    // El "tipo" real (MP/PT/PP/ME/IP/AF/SE/ST) no viaja en las opciones del <select> (esas
    // solo tienen key/value para el combo), asi que se resuelve del catalogo crudo guardado
    // en inventoryTypesRaw.
    const selectedInventoryTypeRaw = inventoryTypesRaw.find(item => `${item.id}` === `${inventoryType}`);
    const isTrackedType = selectedInventoryTypeRaw?.type === 'ME';
    const selectedCategory = categories.find(item => `${item.id}` === `${category}`);
    // La unidad de medida ya no es un catalogo independiente (ver punto 1 pedido por el
    // usuario): viene definida de antemano en el propio articulo (unitId/unitName, expuestos
    // por Get_Provider_Articles), asi que solo se muestra, no se elige.
    const selectedArticle = articles.find(item => `${item.id}` === `${article}`);
    const needsIndividualRegistration = isTrackedType && !!selectedCategory?.individualRegistration;
    const needsManufactureDate = isTrackedType && !!selectedCategory?.manufactureDateRequired;
    const needsExpirationDate = isTrackedType && !!selectedCategory?.expirationDateRequired;
    // No todos los articulos tienen numero de serie: dbo.ftArticuloPoseeInfoComplementaria
    // (expuesta como hasSerialInfo en Get_Provider_Articles) es la fuente de verdad pedida por
    // el usuario para decidir si el subformulario aplica, en vez de recalcular en el cliente
    // el OR de las banderas de categoria (registroindividual/fechafabricacion/
    // fechavencimiento arriba solo deciden QUE columnas mostrar una vez que el subformulario
    // ya se sabe que aplica).
    const needsSerialForm = isTrackedType && !!selectedArticle?.hasSerialInfo;
    // Un "slot" de atributo puede existir en ciarticulosatributos sin tener NINGUN valor
    // definido todavia en ciatributosdominios (el catalogo de dominios para ese atributo esta
    // vacio): en ese caso no hay nada que el usuario pueda elegir, asi que ese slot no cuenta
    // como atributo pedible -- ni se muestra, ni se exige, ni se manda al crear (ver los tres
    // usos de attributeSlotsWithDomains mas abajo). Antes se usaba attributeSlots directo, lo
    // que forzaba a completar un campo que ni siquiera tenia opciones para elegir.
    const attributeSlotsWithDomains = attributeSlots.filter(slot => attributeDomains.some(item => `${item.articleAttributeId}` === `${slot.id}`));
    const needsAttributesForm = isTrackedType && attributeSlotsWithDomains.length > 0;
    const serialQuantitySum = serialRows.reduce((total, row) => total + (parseFloat(row.quantity) || 0), 0);

    // Intercepta CADA llamada a setValue que hace InputFieldCustom para el campo Costo (ver
    // comentario identico en AccountingExpenseCreate.jsx sobre por que se valida en el setter
    // y no en un useEffect sobre "amount"). "saldo pendiente" es invoiceAmount menos lo ya
    // registrado entre integraciones de inventario Y gastos contables de la misma factura
    // (registeredAmount, ver comentario donde se calcula), no el monto total de la factura solo.
    const setAmountGuarded = (rawValue) => {
        var enteredAmount = parseFloat(String(rawValue).replace(/[^0-9.-]/g, '')) || 0;
        var remainingAmount = invoiceAmount === null ? null : (parseFloat(invoiceAmount) - registeredAmount);
        if (remainingAmount !== null && enteredAmount > remainingAmount + 0.005) {
            // Se deja el campo en el saldo pendiente (el maximo permitido) en vez de en 0.00,
            // para que quede claro cual es el limite en vez de simplemente vaciar el campo.
            notify(`El costo (Q ${enteredAmount.toFixed(2)}) supera el saldo pendiente por registrar de la factura (Q ${remainingAmount.toFixed(2)}). Se ajustó al máximo disponible.`, { type: 'warning' });
            setAmount(Math.max(remainingAmount, 0).toFixed(2));
            return;
        }
        setAmount(rawValue);
    };

    // Si el costo BAJA despues de que ya se capturo un no-afecto de IVA/ISR (p.ej. el usuario
    // lo edita directamente, o setAmountGuarded lo recorta al saldo pendiente de la factura),
    // los guards de los campos de no-afecto (mas abajo) no se enteran porque solo corren
    // cuando se escribe EN ESOS campos, no cuando cambia el costo. Este efecto reacciona a
    // cada cambio de "amount" y recorta vatExemptAmount/isrExemptAmount si quedaron por encima
    // del nuevo costo. Usa la forma funcional de setState (prev => ...) para no depender de
    // vatExemptAmount/isrExemptAmount en el arreglo de dependencias: si se agregaran ahi, el
    // efecto se volveria a disparar cada vez que estos mismos setters lo cambian, generando un
    // ciclo de re-ejecuciones innecesario.
    useEffect(() => {
        var costLimit = parseFloat(amount.toString().replace(/[^0-9.-]/g, '')) || 0;

        setVatExemptAmount(prev => {
            var enteredVat = parseFloat(String(prev).replace(/[^0-9.-]/g, '')) || 0;
            if (enteredVat > costLimit + 0.005) {
                notify(`El monto no afecto a IVA (Q ${enteredVat.toFixed(2)}) supera el nuevo costo (Q ${costLimit.toFixed(2)}). Se ajustó al máximo disponible.`, { type: 'warning' });
                return costLimit.toFixed(2);
            }
            return prev;
        });

        setIsrExemptAmount(prev => {
            var enteredIsr = parseFloat(String(prev).replace(/[^0-9.-]/g, '')) || 0;
            if (enteredIsr > costLimit + 0.005) {
                notify(`El monto no afecto a ISR (Q ${enteredIsr.toFixed(2)}) supera el nuevo costo (Q ${costLimit.toFixed(2)}). Se ajustó al máximo disponible.`, { type: 'warning' });
                return costLimit.toFixed(2);
            }
            return prev;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [amount]);

    // Monto No Afecto IVA / ISR son porciones DEL costo (la parte del costo que no esta afecta
    // a cada impuesto), asi que ninguno de los dos puede ser mayor que el costo mismo. Mismo
    // patron de "avisar y ajustar al maximo permitido" que setAmountGuarded, pero el limite aca
    // es el costo capturado arriba, no el saldo de la factura.
    const setVatExemptAmountGuarded = (rawValue) => {
        var enteredAmount = parseFloat(String(rawValue).replace(/[^0-9.-]/g, '')) || 0;
        var costLimit = parseFloat(amount.toString().replace(/[^0-9.-]/g, '')) || 0;
        if (enteredAmount > costLimit + 0.005) {
            notify(`El monto no afecto a IVA (Q ${enteredAmount.toFixed(2)}) no puede ser mayor al costo (Q ${costLimit.toFixed(2)}). Se ajustó al máximo disponible.`, { type: 'warning' });
            setVatExemptAmount(costLimit.toFixed(2));
            return;
        }
        setVatExemptAmount(rawValue);
    };

    const setIsrExemptAmountGuarded = (rawValue) => {
        var enteredAmount = parseFloat(String(rawValue).replace(/[^0-9.-]/g, '')) || 0;
        var costLimit = parseFloat(amount.toString().replace(/[^0-9.-]/g, '')) || 0;
        if (enteredAmount > costLimit + 0.005) {
            notify(`El monto no afecto a ISR (Q ${enteredAmount.toFixed(2)}) no puede ser mayor al costo (Q ${costLimit.toFixed(2)}). Se ajustó al máximo disponible.`, { type: 'warning' });
            setIsrExemptAmount(costLimit.toFixed(2));
            return;
        }
        setIsrExemptAmount(rawValue);
    };

    const PostInventoryIntegration = async () => {
        // "reasons" acumula UNA descripcion legible por cada validacion que falla, en vez del
        // contador "control" original que solo contaba cuantas fallaban sin decir cuales: con
        // varios subformularios opcionales (atributos, numero de serie) activos a la vez, un
        // mensaje generico ("revise los campos obligatorios") no alcanzaba para saber cual de
        // todos los campos era el problema. Ahora el mensaje final lista los motivos reales.
        const reasons = [];

        if (!invoiceId) {
            setMessage("No se pudo determinar la factura de esta integración de inventario");
            handleClickError();
            return;
        }

        if (!Validate(inventoryType, { required: true }))
            reasons.push('Inventario');

        if (!Validate(article, { required: true }))
            reasons.push('Artículo');

        if (!Validate(warehouse, { required: true }))
            reasons.push('Almacén');

        if (!Validate(reason, { required: true }))
            reasons.push('Razón');

        if (!Validate(quantity, { money: true, required: true }) || parseFloat(quantity) <= 0)
            reasons.push('Cantidad');

        if (!Validate(amount, { money: true, required: true }) || parseFloat(amount) <= 0)
            reasons.push('Costo');

        if (!Validate(isrCode, { required: true }))
            reasons.push('Concepto Retención ISR');

        if (!Validate(booksFlag, { required: true }))
            reasons.push('Clasificación');

        // sp_Alta_cbfacturainteginv exige que exists(select 1 from @atributos) cuando el
        // articulo tiene filas en ciarticulosatributos: se valida que cada "slot" CON dominios
        // para elegir tenga un valor capturado antes de enviar (attributeSlotsWithDomains, no
        // attributeSlots: un slot sin dominios no tiene nada que el usuario pueda elegir).
        if (needsAttributesForm) {
            for (const slot of attributeSlotsWithDomains) {
                const entry = attributeValues[slot.id];
                if (!entry || !entry.domainId || !Validate(entry.value, { required: true })) {
                    reasons.push(`Atributo "${slot.label || slot.name}"`);
                }
            }
        }

        // sp_Alta_cbfacturainteginv exige que la suma de @complemento.cantidad (para el/los
        // campo(s) que la categoria requiera) sea exactamente igual a @cantidad.
        if (needsSerialForm) {
            const enteredQuantity = parseFloat(quantity) || 0;
            if (Math.abs(serialQuantitySum - enteredQuantity) > 0.00000001) {
                reasons.push(`la suma de Cantidad por número de serie (${serialQuantitySum}) no coincide con la Cantidad (${enteredQuantity})`);
            }
            serialRows.forEach((row, index) => {
                if (!Validate(row.quantity, { money: true, required: true }) || parseFloat(row.quantity) <= 0) {
                    reasons.push(`Cantidad de la fila ${index + 1} de Números de Serie`);
                }
                if (needsIndividualRegistration && !Validate(row.serialNumber, { required: true })) {
                    reasons.push(`Número de Serie de la fila ${index + 1}`);
                }
                if (needsManufactureDate && !Validate(row.manufactureDate, { required: true })) {
                    reasons.push(`Fabricación de la fila ${index + 1}`);
                }
                if (needsExpirationDate && !Validate(row.expirationDate, { required: true })) {
                    reasons.push(`Vencimiento de la fila ${index + 1}`);
                }
            });
        }

        if (reasons.length > 0) {
            setMessage("No se pudo crear la Integración de Inventario. Revise: " + reasons.join(', ') + '.');
            handleClickError();
            return;
        }

        // Se pueden crear varias integraciones de inventario parciales sobre la misma factura
        // (el trigger de BD va acumulando cbfactura.montointegracioninventarios con cada una),
        // pero ninguna debe hacer que la suma supere el monto de la factura. La BD no valida
        // esto, asi que se valida aca antes de enviar a crear (mismo patron que
        // AccountingExpenseCreate.jsx, con su propio saldo independiente).
        var enteredAmount = parseFloat(amount.toString().replace(/[^0-9.-]/g, '')) || 0;
        var remainingAmount = invoiceAmount === null ? null : (parseFloat(invoiceAmount) - registeredAmount);
        if (remainingAmount !== null && enteredAmount > remainingAmount + 0.005) {
            setMessage(`El costo (Q ${enteredAmount.toFixed(2)}) supera lo que falta por registrar de la factura (Q ${remainingAmount.toFixed(2)}).`);
            handleClickError();
            return;
        }

        // Re-chequeo de seguridad: setVatExemptAmountGuarded/setIsrExemptAmountGuarded ya
        // impiden capturar un valor mayor al costo, pero el costo puede haber bajado DESPUES
        // (p.ej. setAmountGuarded lo ajusto al saldo pendiente), dejando un no-afecto que ya
        // no es valido para el nuevo costo.
        var enteredVatExempt = parseFloat(vatExemptAmount.toString().replace(/[^0-9.-]/g, '')) || 0;
        var enteredIsrExempt = parseFloat(isrExemptAmount.toString().replace(/[^0-9.-]/g, '')) || 0;
        if (enteredVatExempt > enteredAmount + 0.005 || enteredIsrExempt > enteredAmount + 0.005) {
            setMessage(`El monto no afecto a IVA/ISR no puede ser mayor al costo (Q ${enteredAmount.toFixed(2)}).`);
            handleClickError();
            return;
        }

        setOpacity(0.3);
        setDisable(true);
        setDisplayLoading('block');

        var info = {
            invoice: invoiceId,
            article,
            warehouse,
            reason,
            quantity: quantity.toString().replace(/[^0-9.-]/g, ''),
            // InventoryIntegrationModel.Unit es string (igual que article/warehouse/reason,
            // que tambien son ids numericos de catalogo pero viajan como texto): unitId es
            // number en SP_Articulo, asi que hay que convertirlo a string explicitamente, o
            // System.Text.Json truena al deserializar (JSON number contra propiedad string).
            unit: selectedArticle?.unitId != null ? `${selectedArticle.unitId}` : null,
            amount: amount.toString().replace(/[^0-9.-]/g, ''),
            vatExemptAmount: vatExemptAmount.toString().replace(/[^0-9.-]/g, ''),
            isrExemptAmount: isrExemptAmount.toString().replace(/[^0-9.-]/g, ''),
            isrCode: isrCode || null,
            booksFlag: booksFlag || null,
            // attributeSlotsWithDomains, no attributeSlots: un slot sin dominios nunca tiene
            // attributeValues[slot.id] (no hay UI para llenarlo), asi que mandarlo igual
            // generaria una fila con domainId='' -- Providersapi.cs hace int.Parse(DomainId)
            // sobre cada fila del TVP @atributos y truena con string vacio.
            attributes: needsAttributesForm
                ? attributeSlotsWithDomains.map(slot => ({
                    articleAttributeId: `${slot.id}`,
                    domainId: `${attributeValues[slot.id]?.domainId || ''}`,
                    value: attributeValues[slot.id]?.value || '',
                }))
                : [],
            serials: needsSerialForm
                ? serialRows.map(row => ({
                    quantity: row.quantity.toString().replace(/[^0-9.-]/g, ''),
                    serialNumber: needsIndividualRegistration ? row.serialNumber : null,
                    manufactureDate: needsManufactureDate ? row.manufactureDate : null,
                    expirationDate: needsExpirationDate ? row.expirationDate : null,
                }))
                : [],
        }

        dataProvider.create('provider/inventoryintegration', { data: info })
            .then(() => {
                setOpacity(1);
                setDisable(false);
                setDisplayLoading('none');
                notify('Integración de Inventario creada correctamente');
                navigate({
                    pathname: '/provider/inventoryintegration',
                    search: `?filter=${encodeURIComponent(JSON.stringify({ invoice: invoiceId }))}&invoiceNumber=${encodeURIComponent(invoiceNumber || '')}`,
                }, { state: { invoiceNumber, invoiceId } });
            })
            .catch((error) => {
                setOpacity(1);
                setDisable(false);
                setDisplayLoading('none');
                setMessage("No se pudo crear la Integración de Inventario. " + error.toString().replace('HttpError: ', ''));
                handleClickError()
            })
    }

    const handleRedirectProviderList = () => {
        navigate(`/provider`);
    };

    const handleRedirectIntegrations = () => {
        navigate({
            pathname: '/provider/inventoryintegration',
            search: `?filter=${encodeURIComponent(JSON.stringify({ invoice: invoiceId }))}&invoiceNumber=${encodeURIComponent(invoiceNumber || '')}`,
        }, { state: { invoiceNumber, invoiceId } });
    };

    const [openProviders, setOpenProviders] = useState(true);

    const handleClickOpenProviders = () => {
        setOpenProviders(!openProviders);
    };

    const menus = [{
        icon: <img src={providers} alt="logo" style={Style.MenuLogo} />, name: 'Proveedores',
        open: openProviders,
        openAction: handleClickOpenProviders,
        actions: [
            { icon: <img src={providerList} alt="logo" style={Style.MenuLogo} />, name: 'Listado', onClick: handleRedirectProviderList },
        ]
    }];

    return (
        <div class="container">
            <BreadCrumbCustom history={historyTracker.getHistory()} />
            {initialLoading ? (
                // Spinner de carga inicial: reutiliza la misma clase "Loading" (index.css) y
                // el mismo CircularProgress que ya se usaba para el guardado (mas abajo,
                // controlado por displayLoading), pero aqui cubre la espera de auth + todos
                // los catalogos, que antes dejaba la pantalla en blanco.
                <div class="Loading" style={{ display: 'block' }}>
                    <CircularProgress size="15rem" />
                </div>
            ) : auth ? (
                // paddingBottom en linea recorta el padding-bottom:2em fijo que trae la clase
                // "edit" (index.css) solo para esta pantalla -- mismo ajuste que
                // AccountingExpenseCreate.jsx/ProviderInvoiceCreate.jsx: con el padding de 2em
                // completo mas el marginTop de arriba (ver mas abajo, antes del boton), quedaba
                // mucho mas aire abajo del boton que arriba. No se toco la clase "edit" en si
                // porque la comparten todas las pantallas de Crear/Show.
                <div class="edit" style={{ paddingBottom: '0.1em' }}>
                    {!isSmall ? (
                        <div class="Loading" style={{ display: displayLoading }}>
                            <CircularProgress size="15rem" />
                        </div>) : (
                        <div class="Loading" style={{ display: displayLoading }}>
                            <CircularProgress size="15rem" />
                        </div>
                    )}
                    {!isSmall ? (
                        <div class="container" style={{ opacity: opacity }}>
                            {invoiceAmount !== null ? (
                                <div class="row">
                                    <div class="col-12">
                                        <label style={Style.Label}>
                                            Saldo pendiente por registrar: Q {(parseFloat(invoiceAmount) - registeredAmount).toLocaleString('es-GT', { style: 'decimal', minimumFractionDigits: 2 })} de Q {parseFloat(invoiceAmount).toLocaleString('es-GT', { style: 'decimal', minimumFractionDigits: 2 })}
                                        </label>
                                    </div>
                                </div>
                            ) : null}
                            <div class="row">
                                <InputFieldCustom col="col-3" width='95%' setValue={setInventoryType} value={inventoryType} options={inventoryTypes} component="select" label="Inventario *" />
                                {classificationLevels.map((level, idx) => (
                                    <InputFieldCustom
                                        key={idx}
                                        col="col-3"
                                        width="95%"
                                        setValue={(value) => handleSelectClassification(idx, value)}
                                        value={level.selectedId}
                                        options={[{ key: '', value: 'Seleccione' }, ...level.options.map(item => ({ key: item.id, value: item.description }))]}
                                        component="select"
                                        label={idx === 0 ? 'Clasificación *' : 'Subclasificación'}
                                    />
                                ))}
                                {categories.length > 0 ? (
                                    <InputFieldCustom col="col-3" width='95%' setValue={handleSelectCategory} value={category} options={[{ key: '', value: 'Seleccione' }, ...categories.map(item => ({ key: item.id, value: item.description }))]} component="select" label="Categoría *" />
                                ) : null}
                                {articles.length > 0 || category ? (
                                    <InputFieldCustom col="col-3" width='95%' setValue={setArticle} value={article} options={[{ key: '', value: 'Seleccione' }, ...articles.map(item => ({ key: item.id, value: `${item.code} - ${item.description}` }))]} component="select" label="Artículo *" />
                                ) : null}
                            </div>

                            <div class="row">
                                <InputFieldCustom col="col-3" width='95%' setValue={setWarehouse} value={warehouse} options={warehouses} component="select" label="Almacén *" />
                                <InputFieldCustom col="col-3" width='95%' setValue={setReason} value={reason} options={reasons} component="select" label="Razón *" />
                                <InputFieldCustom value={quantity} setValue={setQuantity} col="col-3" width="95%" placeholder="Ingresa la cantidad" label="Cantidad *" required type="text" />
                                {/* Unidad de Medida ya no es un catalogo elegible: viene predefinida en el
                                    articulo seleccionado (unitName, ver Get_Provider_Articles), asi que solo
                                    se muestra en modo lectura (readOnly), sin setValue funcional. */}
                                <InputFieldCustom value={selectedArticle?.unitName || ''} setValue={() => {}} col="col-3" width="95%" label="Unidad de Medida" type="text" readOnly />
                            </div>
                            <div class="row">
                                <InputFieldCustom value={amount} setValue={setAmountGuarded} col="col-3" width="95%" placeholder="Ingresa el costo" label="Costo (Q) *" required type="text" money />
                                <InputFieldCustom value={vatExemptAmount} setValue={setVatExemptAmountGuarded} col="col-3" width="95%" placeholder="Ingresa el monto" label="Monto No Afecto IVA (Q)" type="text" money />
                                <InputFieldCustom value={isrExemptAmount} setValue={setIsrExemptAmountGuarded} col="col-3" width="95%" placeholder="Ingresa el monto" label="Monto No Afecto ISR (Q)" type="text" money />
                            </div>
                            <div class="row">
                                <InputFieldCustom col="col-3" width='95%' setValue={setIsrCode} value={isrCode} options={isrConcepts} component="select" label="Concepto Retención ISR *" />
                                <InputFieldCustom col="col-3" width='95%' setValue={setBooksFlag} value={booksFlag} options={BooksFlagOptions} component="select" label="Clasificación *" />
                            </div>
                            {needsAttributesForm ? (
                                <div class="row" style={{ marginTop: '1.5em' }}>
                                    <div class="col-12">
                                        <label style={Style.Label}>Atributos del Artículo</label>
                                    </div>
                                    {attributeSlotsWithDomains.map((slot) => {
                                        const slotDomains = attributeDomains.filter(item => `${item.articleAttributeId}` === `${slot.id}`);
                                        const freeDomain = slotDomains.find(item => item.type === 'L');
                                        const selectDomains = slotDomains.filter(item => item.type !== 'L');
                                        const entry = attributeValues[slot.id] || { domainId: '', value: '' };

                                        if (freeDomain) {
                                            const setFreeValue = (value) => {
                                                setAttributeValues(prev => ({
                                                    ...prev,
                                                    [slot.id]: { domainId: `${freeDomain.domainId}`, value },
                                                }));
                                            };
                                            return (
                                                <InputFieldCustom
                                                    key={slot.id}
                                                    col="col-3"
                                                    width="95%"
                                                    value={entry.value}
                                                    setValue={setFreeValue}
                                                    placeholder="Ingresa el valor"
                                                    label={slot.label || slot.name}
                                                    type="text"
                                                />
                                            );
                                        }

                                        const setSelectedDomain = (value) => {
                                            const chosen = selectDomains.find(item => `${item.domainId}` === `${value}`);
                                            setAttributeValues(prev => ({
                                                ...prev,
                                                [slot.id]: { domainId: value, value: chosen ? chosen.value : '' },
                                            }));
                                        };

                                        return (
                                            <InputFieldCustom
                                                key={slot.id}
                                                col="col-3"
                                                width="95%"
                                                value={entry.domainId}
                                                setValue={setSelectedDomain}
                                                options={[{ key: '', value: 'Seleccione' }, ...selectDomains.map(item => ({ key: item.domainId, value: item.value }))]}
                                                component="select"
                                                label={slot.label || slot.name}
                                            />
                                        );
                                    })}
                                </div>
                            ) : null}
                            {needsSerialForm ? (
                                <div class="row" style={{ marginTop: '1.5em' }}>
                                    <div class="col-12">
                                        <label style={Style.Label}>
                                            Números de Serie {quantity ? `(suma: ${serialQuantitySum.toLocaleString('es-GT', { minimumFractionDigits: 2 })} de ${(parseFloat(quantity) || 0).toLocaleString('es-GT', { minimumFractionDigits: 2 })})` : ''}
                                        </label>
                                        {/* Cada columna de dato se limita al 25% del ancho de la tabla (misma
                                            proporcion que un col-3 de Bootstrap dentro del .container de la
                                            pagina, que ocupa el 100% del ancho -- ver index.css/public/index.html)
                                            y el input adentro va al 95%, igual que InputFieldCustom: asi una
                                            columna de esta tabla queda del mismo tamaño visual que un campo
                                            InputFieldCustom col-3 del resto del formulario. No se usa
                                            table-layout:fixed a proposito -- con hasta 4 columnas de dato
                                            (25% c/u = 100%) no dejaria espacio para la columna de "Quitar";
                                            en layout automatico el ancho del input ya angosto es suficiente
                                            para que el navegador respete el 25% como un maximo razonable. */}
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell style={{ ...Style.DataGrid, width: '25%' }}>Cantidad</TableCell>
                                                    {needsIndividualRegistration ? <TableCell style={{ ...Style.DataGrid, width: '25%' }}>Número de Serie</TableCell> : null}
                                                    {needsManufactureDate ? <TableCell style={{ ...Style.DataGrid, width: '25%' }}>Fabricación</TableCell> : null}
                                                    {needsExpirationDate ? <TableCell style={{ ...Style.DataGrid, width: '25%' }}>Vencimiento</TableCell> : null}
                                                    <TableCell style={Style.DataGrid}></TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {serialRows.map((row, index) => (
                                                    <TableRow key={index}>
                                                        <TableCell style={{ width: '25%' }}>
                                                            <input className="InputField" type="text" placeholder="Cantidad" value={row.quantity} onChange={(e) => updateSerialRow(index, 'quantity', e.target.value)} style={{ width: '95%' }} />
                                                        </TableCell>
                                                        {needsIndividualRegistration ? (
                                                            <TableCell style={{ width: '25%' }}>
                                                                <input className="InputField" type="text" placeholder="Número de Serie" value={row.serialNumber} onChange={(e) => updateSerialRow(index, 'serialNumber', e.target.value)} style={{ width: '95%' }} />
                                                            </TableCell>
                                                        ) : null}
                                                        {needsManufactureDate ? (
                                                            <TableCell style={{ width: '25%' }}>
                                                                <input className="InputField" type="date" value={row.manufactureDate} onChange={(e) => updateSerialRow(index, 'manufactureDate', e.target.value)} style={{ width: '95%' }} />
                                                            </TableCell>
                                                        ) : null}
                                                        {needsExpirationDate ? (
                                                            <TableCell style={{ width: '25%' }}>
                                                                <input className="InputField" type="date" value={row.expirationDate} onChange={(e) => updateSerialRow(index, 'expirationDate', e.target.value)} style={{ width: '95%' }} />
                                                            </TableCell>
                                                        ) : null}
                                                        <TableCell>
                                                            {/* color="secondary" se veia invisible: App.jsx sobreescribe el "secondary"
                                                                del tema a blanco (#ffffff) para el fondo del AppBar, asi que un boton
                                                                outlined con ese color queda blanco sobre blanco. "error" (rojo) no esta
                                                                sobreescrito y es la convencion normal de MUI para una accion de eliminar;
                                                                se mantiene ese color (en vez de Style.ToolBarButton completo) pero con la
                                                                tipografia/textTransform/borderRadius de la plataforma, para que no quede
                                                                en mayusculas con la fuente por defecto de MUI como antes. */}
                                                            <Button onClick={() => removeSerialRow(index)} disabled={serialRows.length === 1} color="error" size="small" variant="outlined" style={{ fontFamily: 'Gothic', textTransform: 'none', borderRadius: '7px' }}>Quitar</Button>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                        <div class="row" style={{ marginTop: '1.5em' }}>
                                            <div class="col-12" style={{ textAlign: 'right' }}>
                                                <Button onClick={addSerialRow} style={{ ...Style.ToolBarButton, width: 'auto', minWidth: '10em' }}>Agregar</Button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : null}
                            {/* Espacio extra antes del boton (ademas del marginTop propio de
                                BottomToolBar) para que no quede pegado a la ultima fila del
                                formulario -- mismo ajuste que AccountingExpenseCreate.jsx/
                                ProviderInvoiceCreate.jsx. */}
                            <div style={{ marginTop: '2em' }}>
                                <BottomToolBar buttons={[{ auth: true, action: PostInventoryIntegration, name: 'Crear' }, { auth: true, action: handleRedirectIntegrations, name: 'Cancelar' }]} disabled={disable} menu={<InternalMenu menus={menus} />} />
                            </div>
                        </div>
                    ) : (
                        <div class="container" style={{ opacity: opacity }}>

                        </div>)
                    }
                    <div class="row" style={Style.Alert}>
                        <div>
                            <Snackbar open={openerror} autoHideDuration={4000} onClose={handleCloseError}>
                                <div>
                                    <Alert onClose={handleCloseError} severity="error" style={Style.AlertMessage} >
                                        {message}
                                    </Alert>
                                </div>
                            </Snackbar>
                        </div>
                    </div>
                </div>) : (<AuthNotification openerror={openAuth} severity={'error'} message={'No posee los permisos para poder interactuar con esta vista'} />)}
        </div>
    )
}

export default InventoryIntegrationCreate
